import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import StatusTimeline from "@/components/StatusTimeline";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_LABEL, STATUS_BADGE, PAYMENT_BADGE } from "@/lib/statusLabels";
import { toast } from "sonner";
import { Upload, Download, Trash2, AlertTriangle, CheckCircle2, FileText, IndianRupee, FileCheck2 } from "lucide-react";

export default function OrderDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("Other");
  const [deliverables, setDeliverables] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setData(data);
      const r = await api.get(`/orders/${id}/deliverables`);
      setDeliverables(r.data.deliverables || []);
    } catch (e) {
      toast.error("Order not found");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.error("File too large (max 15MB)");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", docType);
    try {
      await api.post(`/orders/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Document deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const download = async (doc) => {
    const token = localStorage.getItem("w2e_token");
    const res = await fetch(`${API_BASE}/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = doc.filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadInvoice = async () => {
    const token = localStorage.getItem("w2e_token");
    const res = await fetch(`${API_BASE}/orders/${id}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Could not generate invoice");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${data?.order.order_number || "invoice"}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDeliverable = async (dv) => {
    const token = localStorage.getItem("w2e_token");
    const res = await fetch(`${API_BASE}/deliverables/${dv.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = dv.filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <DashboardLayout><div className="text-slate-500">Loading…</div></DashboardLayout>;

  const { order, documents, history, service } = data;
  const hasInvalid = documents.some((d) => d.status === "invalid" || d.status === "missing");

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/dashboard/orders" className="text-sm text-slate-500 hover:text-[#1E3A8A]">← All orders</Link>
          <div className="flex items-center gap-3 flex-wrap mt-2">
            <h1 className="font-display font-bold text-3xl text-[#0A192F] font-mono">{order.order_number || "Pending"}</h1>
            <span className={`status-badge ${STATUS_BADGE[order.current_status]}`}>{STATUS_LABEL[order.current_status]}</span>
            <span className={`status-badge ${PAYMENT_BADGE[order.payment_status]}`}>Payment: {order.payment_status}</span>
          </div>
          <div className="text-slate-600 mt-1">{order.service_name}</div>
        </div>
        {order.payment_status === "paid" && (
          <Button variant="outline" onClick={downloadInvoice} data-testid="download-invoice-btn">
            <Download className="w-4 h-4 mr-1" /> Invoice PDF
          </Button>
        )}
      </div>

      {hasInvalid && (
        <div className="mb-6 p-4 rounded-lg border border-amber-300 bg-amber-50 flex gap-3 items-start" data-testid="action-required-banner">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900">Action required</div>
            <div className="text-sm text-amber-800">Some documents need your attention. Please replace them below.</div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
        <div className="section-label mb-4">Document Status Tracker</div>
        <StatusTimeline current={order.current_status} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Documents */}
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="p-5 border-b border-slate-200 flex justify-between items-start flex-wrap gap-3">
              <div>
                <div className="section-label">Documents</div>
                <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Upload required files</div>
              </div>
              {order.payment_status === "paid" && (
                <div className="flex gap-2 items-center">
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger className="w-44" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(service?.required_documents || ["Other"]).map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={upload} className="hidden" disabled={uploading} data-testid="doc-upload-input" />
                    <Button asChild={false} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" disabled={uploading} onClick={(e) => e.currentTarget.previousElementSibling.click()} data-testid="doc-upload-btn">
                      <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading…" : "Upload"}
                    </Button>
                  </label>
                </div>
              )}
            </div>

            {service?.required_documents?.length > 0 && (
              <div className="p-5 border-b border-slate-200">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Required</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {service.required_documents.map((d) => {
                    const has = documents.find((x) => x.document_type === d);
                    return (
                      <div key={d} className={`p-3 rounded-md border text-sm flex items-center gap-2 ${has ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                        {has ? <CheckCircle2 className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                        <span className="truncate">{d}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="p-5">
              {documents.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  {order.payment_status === "paid" ? "No documents uploaded yet." : "Complete payment to upload documents."}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {documents.map((d) => (
                    <li key={d.id} className="py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[#0A192F] truncate">{d.filename}</span>
                          <span className={`status-badge ${
                            d.status === "approved" ? "badge-success" :
                            d.status === "invalid" || d.status === "missing" ? "badge-error" : "badge-info"
                          }`}>{d.status}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {d.document_type} • {(d.size / 1024).toFixed(1)} KB • {new Date(d.uploaded_at).toLocaleString()}
                        </div>
                        {d.admin_reason && (d.status === "invalid" || d.status === "missing") && (
                          <div className="text-xs text-red-600 mt-1">Reason: {d.admin_reason}</div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => download(d)} data-testid={`doc-dl-${d.id}`}><Download className="w-3.5 h-3.5" /></Button>
                        {d.status !== "approved" && (
                          <Button variant="outline" size="sm" onClick={() => remove(d.id)} data-testid={`doc-rm-${d.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Deliverables from admin */}
          {deliverables.length > 0 && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5" data-testid="deliverables-panel">
              <div className="section-label text-emerald-700">Delivered by Way2Efiling</div>
              <div className="font-display font-semibold text-lg text-[#0A192F] mt-1 mb-3">Your completed documents & receipts</div>
              <ul className="divide-y divide-emerald-100">
                {deliverables.map((dv) => (
                  <li key={dv.id} className="py-3 flex items-center justify-between gap-3" data-testid={`deliverable-item-${dv.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileCheck2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span className="font-semibold text-[#0A192F] truncate">{dv.label}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {dv.filename} • {(dv.size / 1024).toFixed(1)} KB • {new Date(dv.uploaded_at).toLocaleString()}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => downloadDeliverable(dv)} data-testid={`deliverable-dl-${dv.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1" /> Download
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* History */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="section-label">History</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Status timeline</div>
            <ol className="mt-4 space-y-3">
              {history.slice().reverse().map((h, i) => (
                <li key={i} className="flex gap-3">
                  <div className="w-2 h-2 mt-1.5 rounded-full bg-[#1E3A8A]" />
                  <div className="flex-1">
                    <div className="text-sm text-[#0A192F] font-medium">
                      {STATUS_LABEL[h.to_status] || h.to_status}
                    </div>
                    <div className="text-xs text-slate-500">{new Date(h.created_at).toLocaleString()} · by {h.changed_by_name || "System"}</div>
                    {h.remarks && <div className="text-xs text-slate-600 mt-0.5">{h.remarks}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="section-label">Order info</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Order Date</span><span>{new Date(order.created_at).toLocaleDateString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-medium">₹{order.amount}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Payment</span><span className={`status-badge ${PAYMENT_BADGE[order.payment_status]}`}>{order.payment_status}</span></div>
              {(order.cashfree_payment_id || order.cashfree_order_id) && (
                <div className="flex justify-between"><span className="text-slate-500">Payment ID</span><span className="font-mono text-xs">{order.cashfree_payment_id || order.cashfree_order_id}</span></div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="section-label">Customer</div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="font-medium text-[#0A192F]">{order.full_name}</div>
              <div className="text-slate-600">{order.email}</div>
              <div className="text-slate-600">{order.mobile}</div>
              {order.pan && <div className="font-mono text-xs text-slate-600">PAN: {order.pan}</div>}
              {order.address && <div className="text-slate-600 text-xs mt-2">{order.address}</div>}
            </div>
          </div>

          <div className="rounded-xl bg-[#0A192F] text-white p-5">
            <div className="text-xs uppercase tracking-widest text-slate-400">Need help?</div>
            <div className="mt-2 font-semibold">Way2Efiling Support</div>
            <a href="tel:8448440219" className="mt-1 block text-sm text-slate-300 hover:text-white">📞 8448440219</a>
            <a href="mailto:support@way2efiling.com" className="block text-sm text-slate-300 hover:text-white">✉ support@way2efiling.com</a>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
